import { BCButton, BCInput, BCSpaceCompact, findMessage } from '@bimser/components';
import { FormItemTypes, IDictionary } from '@bimser/core';
import IconImport from '@bimser/icons/16/import';
import dayjs from 'dayjs';
import React, { JSX } from 'react';
import { DocumentMetadataType } from '../../../../entities';
import { IPropertyField, SizeType } from '../../../../entities/common';
import MetadataControl from '../../base/MetadataControl';
import Styles from './assets/styles.scss';
import IDocumentMetadataProperties from './entities/IDocumentMetadataProperties';
import { PullNewNumber } from './helpers/PullNewNumber';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(localizedFormat);

const inputFullWidthStyle: React.CSSProperties = {
	width: '100%',
	paddingTop: '0px',
	paddingBottom: '0px'
};
const inputStyle: React.CSSProperties = {
	width: 'calc(100% - 35px)',
	paddingTop: '0px',
	paddingBottom: '0px'
};
const buttonStyle: React.CSSProperties = { width: '35px', fontWeight: 900, fontSize: '14px' };
const transitionReset: React.CSSProperties = { transition: 'all 0.3s, width 0s' };

export default class DocumentMetadata extends MetadataControl<DocumentMetadataType> {
	dateFormat: string;
	sizeType: SizeType;
	pullNumberOnStart: boolean;
	pullNewNumber: boolean;

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.DocumentMetadata;
		this.metadataType = 'Id';
		this.pullNumberOnStart = false;
		this.pullNewNumber = false;

		this.__pullNewNumber = this.__pullNewNumber.bind(this);
		this.subscribe('trigger:OnClickPullButton', () => {
			this.__pullNewNumber();
		});
	}

	__loadProperties(data: IDocumentMetadataProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.dateFormat = data.dateFormat ? data.dateFormat : 'L LTS';
		this.__proxyChanged.sizeType = data.sizeType;
		this.__proxyChanged.pullNumberOnStart = data.pullNumberOnStart;
		this.__proxyChanged.pullNewNumber = data.pullNewNumber;
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'dateFormat',
				type: 'string'
			}
		];
	}

	__getValue(): string {
		if (this.metadataType === 'CreateDate' && dayjs(this.text).isValid()) {
			return dayjs(this.text).format(this.dateFormat).toString();
		} else if (this.metadataType === 'Id' && this.text) {
			return this.text.toString();
		}
		return '';
	}

	__pullNewNumber() {
		try {
			PullNewNumber(this.__form.projectName, this.__form.name).then((result: any) => {
				this.__proxyChanging.text = result.data;
				this.__proxyChanging.value = result.data;
				this.__proxyChanging.readOnly = true;
				this.__proxyChanging.pullNewNumber = false;
			});
		} catch (error) {
			console.log('DocumentMetadata pull number error', error);
		}
	}

	__renderButtons() {
		if (this.__isDisabled() || !this.pullNewNumber || this.text) {
			return undefined;
		}
		return (
			<>
				<BCButton
					style={{ ...buttonStyle, ...{ backgroundColor: this.style.backgroundColor } }}
					onClick={this.__pullNewNumber}
					size={this.sizeType}
					icon={<IconImport />}
					title={findMessage.get('102388')}
					disabled={this.readOnly}
				/>
			</>
		);
	}

	__getInputStyle() {
		const customStyles: React.CSSProperties = { ...inputStyle };

		if (this.__isDisabled() || !this.pullNewNumber || this.text) {
			return { ...inputFullWidthStyle, ...this.style, ...transitionReset };
		} else {
			return { ...customStyles, ...this.style, ...transitionReset };
		}
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			dateFormat: this.dateFormat,
			sizeType: this.sizeType,
			pullNumberOnStart: this.pullNumberOnStart,
			pullNewNumber: this.pullNewNumber,
			value: this.__getValue()
		};
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<BCSpaceCompact
				block
				size={this.__getSizeType()}
			>
				<BCInput
					style={this.__getInputStyle()}
					value={this.__getValue()}
					readOnly
					disabled={this.__isDisabled()}
					className={Styles.transparentContent}
				/>
				{this.__renderButtons()}
			</BCSpaceCompact>
		);
	}
}
